import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { CompanyDTO, CompanyFilterDTO, OpeningDTO, OpeningFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import { People } from '../people/People';
import { OpeningMaintenance } from './OpeningMaintenance';
import { Product } from '../product/Product';
import { Report } from './Report';

export const Opening: React.FC = () => {

    const [salesPerscpectiveList, setSalesPerspectiveList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [openingFilterDTO, setOpeningFilterDTO] = useState<OpeningFilterDTO>();
    const [openingList, setOpeningList] = useState<OpeningDTO[]>([]);

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [product, setProduct] = useState('');
    const [report, setReport] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setOpeningFilterDTO({
            ...openingFilterDTO,
            id:'',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            companyId: '',
        } as OpeningFilterDTO);
    }, []);

    useEffect(() => {
        handleListGetAll(ListId.corpsphere.opening.salesPerspective, setSalesPerspectiveList);
        handleCompanyGetAll();
        handleOpeningGetAllViewAsync();
    }, [openingFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleOpeningGetAllViewAsync();
    }, [actionExecuted]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);
    
    const handleOpeningGetAllViewAsync = useCallback(async () => {
        if (openingFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('corpsphere.opening', openingFilterDTO, setActionExecuted, setLoading, setOpeningList, showMessage, true);
    }, [openingFilterDTO]);

    const handleClearFilterClick = () => {
        setOpeningFilterDTO({
            ...openingFilterDTO,
            id:'',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            companyId: '',
        } as OpeningFilterDTO);
    };

    function handleSearchClick() {
        handleOpeningGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setReport('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setReport('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setReport('');
        setIsOpenMaintenance(true);
    };

    const handleReport = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setProduct('');
        setReport(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Opening', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'openingReport', toolTip: 'Opening Report', icon: 'summarize_outlined', iconColor: 'info', setAction: handleReport },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: openingList.length },
        { label: 'Laboratory', value: openingFilterDTO?.laboratory },
        { label: 'Sales Perspesctive', value: openingFilterDTO?.salePerspectiveId },
        { label: 'Company', value: companyList.find(x => x.id === openingFilterDTO?.companyId)?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'code', label: 'Code', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'laboratory', label: 'Laboratory', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'salePerspective', label: 'Sales Perspective', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'developmentDeadline', label: 'Development Deadline', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'company', label: 'Customer', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'creationDateTime', label: 'Creation Date', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        // { field: 'description', label: 'Description', size: 12, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='COMPANY'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='laboratory'
                                label='Laboratory'
                                setValue={setOpeningFilterDTO}
                                value={openingFilterDTO?.laboratory}
                                obj={openingFilterDTO}
                                placeholder={'Laboratory'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='salePerspectiveId'
                                label='Sales Perspective'
                                setValue={setOpeningFilterDTO}
                                value={openingFilterDTO?.salePerspectiveId}
                                obj={openingFilterDTO}
                                valueList={salesPerscpectiveList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                setValue={setOpeningFilterDTO}
                                value={openingFilterDTO?.companyId}
                                obj={openingFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={openingList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'Associate Products'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <Product data={expadedData} />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <OpeningMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <OpeningMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'corpsphere.company'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
                {report && (
                    <Report
                        data={report}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};